/**
 * The replay strip (docs/ux/casefile.md): the mode chip, the run sheet's
 * sentence "Replay of run <id>, <date>: N pages, M min, K review items", and
 * the two viewers: "View Unsorted Case Files" (the bundle as received) and
 * "View Sorted Case Files" (the working casefile, adjusting as decisions are
 * saved), both modal (D-0081). No usage, token or cost field, ever.
 */

import type { SortRunHead } from '@/app/types';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { longDate, dateTime } from '@/lib/format';
import { wallClockText } from './describe';
import { Button } from '@/components/ui/button';
import type { ViewerMode } from './ViewerDialog';

export function OpenPdfLink({ href, children, variant = 'link', className }: { href: string; children: string; variant?: 'link' | 'outline'; className?: string }) {
  const base = variant === 'outline'
    ? 'inline-flex h-11 items-center justify-center rounded-lg border border-beam-field bg-beam-paper px-3 text-sm font-medium hover:bg-beam-cream-deep md:h-9'
    : 'inline-flex min-h-11 items-center text-sm font-medium text-beam-signature underline-offset-4 hover:underline md:min-h-0';
  return (
    <a href={href} target="_blank" rel="noopener" className={`${base} ${className ?? ''}`}>
      {children}<span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

export function ReplayPanel({ head, onView }: { head: SortRunHead; onView: (mode: ViewerMode) => void }) {
  const m = head.machine;
  // A sorter run id is a timestamp and a hash; the tooltip carries the whole of it.
  const shortId = head.runId.length > 24 ? head.runId.slice(0, 16) : head.runId;
  return (
    <section aria-label="Sort run" data-slot="replay-panel" className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-beam-line bg-beam-paper px-4 py-2 text-[13px]">
      <Badge variant="outline" className="h-6 border-transparent bg-beam-warning-bg px-2.5 text-xs text-beam-warning">{m.modeLabel}</Badge>
      <span>
        Replay of run{' '}
        <Tooltip>
          <TooltipTrigger asChild><span tabIndex={0} className="tabular cursor-default rounded-sm underline decoration-dotted underline-offset-4 outline-none focus-visible:ring-3 focus-visible:ring-ring/50">{shortId}</span></TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="tabular">{head.runId}</p>
            <p>Started {dateTime(m.startedAt)}, completed {dateTime(m.completedAt)}. {m.documents} documents, {m.duplicatePages} duplicate {m.duplicatePages === 1 ? 'page' : 'pages'}, {m.billablePages} billable pages.</p>
          </TooltipContent>
        </Tooltip>
        , {longDate(m.completedAt)}: <span className="tabular">{m.totalPages}</span> pages, {wallClockText(m.wallClockMs)}, <span className="tabular">{m.reviewItems}</span> review {m.reviewItems === 1 ? 'item' : 'items'}
      </span>
      <span className="ml-auto flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onView('unsorted')}>View Unsorted Case Files</Button>
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onView('sorted')}>View Sorted Case Files</Button>
      </span>
    </section>
  );
}
